import type { ConnectAccountStatus } from "@afar-research/contracts";
import { useCallback, useEffect, useState } from "react";

import { readLocalApi } from "../localApi";

/**
 * The AFAR Product Account this client is signed in to, or null when there is no Connect bridge.
 * Settings gates on this rather than on `isElectron`, because what a section actually needs is an
 * account that can authenticate it — not a particular shell (#807).
 */
export function useConnectBridgeAvailable(): boolean {
  return readLocalApi()?.connect !== undefined;
}

/**
 * The account status, and a way to ask for it again.
 *
 * Read once on mount, because nothing here polls: the status only changes when the user signs in
 * or out, or when a call discovers the credential is spent. `refresh` is for that second case —
 * a surface that has just been refused re-reads rather than keeping the answer it had, which is
 * how the Feedback panel avoids claiming a sign-in it no longer has (#1008).
 */
export function useConnectAccount(): {
  readonly account: ConnectAccountStatus | null;
  readonly refresh: () => void;
} {
  const [status, setStatus] = useState<ConnectAccountStatus | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const connect = readLocalApi()?.connect;
    if (!connect) return;
    let disposed = false;
    void connect.getStatus().then(
      (next) => {
        if (!disposed) setStatus(next);
      },
      () => {
        if (!disposed) setStatus(null);
      },
    );
    return () => {
      disposed = true;
    };
  }, [revision]);
  const refresh = useCallback(() => setRevision((current) => current + 1), []);
  return { account: status, refresh };
}
